import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Spin } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import styled from 'styled-components';
import { useTranslationWithNamespace } from '../hooks/useTranslationWithNamespace';
import LanguageSwitcher from '../components/common/LanguageSwitcher';

const { Title } = Typography;

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f2f5;
`;

const StyledCard = styled(Card)`
  width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Logo = styled.img`
  height: 64px;
  margin-bottom: 16px;
`;

const LanguageSwitcherContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const LoginPage: React.FC = () => {
  const { t } = useTranslationWithNamespace('login');
  const { login, isLoading, error } = useAuth();
  const [form] = Form.useForm();
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleSubmit = async (values: { username: string; password: string }) => {
    setLoginError(null);
    const success = await login(values.username, values.password);
    if (!success) {
      setLoginError(error || t('login.invalidCredentials'));
    }
  };

  return (
    <LoginContainer>
      <StyledCard>
        <Spin spinning={isLoading}>
          <LogoContainer>
            <Logo src="/logo.png" alt="BitAzar Logo" />
            <Title level={3}>{t('app.name')}</Title>
            <Title level={5}>{t('login.title')}</Title>
          </LogoContainer>

          {loginError && (
            <Alert
              message={t('login.error')}
              description={loginError}
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Form
            form={form}
            name="login"
            initialValues={{ remember: true }}
            onFinish={handleSubmit}
            layout="vertical"
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: t('login.usernameRequired') }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t('login.username')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: t('login.passwordRequired') }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('login.password')}
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={isLoading}
              >
                {t('login.signIn')}
              </Button>
            </Form.Item>
          </Form>

          <LanguageSwitcherContainer>
            <LanguageSwitcher />
          </LanguageSwitcherContainer>
        </Spin>
      </StyledCard>
    </LoginContainer>
  );
};

export default LoginPage;
